import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Empty } from "antd";
import styled from "styled-components";
import dayjs from "dayjs";
import SpendingCard from "./SpendingCard";
import { summarizeByCurrency, formatAmount, weekdayLabel } from "../utils/spending";

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 1em 0.25em 0.5em;
  color: #274863;
`;

const DayLeft = styled.div`
  display: flex;
  align-items: baseline;

  .weekday {
    margin-left: 0.5em;
    font-size: 12px;
    color: #6b8299;
  }
`;

const DayTotals = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 13px;
`;

const TotalLine = styled.div`
  .label {
    margin-right: 0.35em;
    color: #6b8299;
  }

  &.income {
    color: #389e0d;
  }
`;

export default function SpendingList({ spendings, onDelete, onUpdate }) {
  const spendingsGroupByDate = useMemo(() => {
    return spendings.reduce((groupByDate, spending) => {
      const date = dayjs(Number(spending.date)).format("YYYY-MM-DD");

      if (groupByDate[date]) {
        return {
          ...groupByDate,
          [date]: [...groupByDate[date], spending],
        };
      }

      return {
        ...groupByDate,
        [date]: [spending],
      };
    }, {});
  }, [spendings]);

  if (spendings.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return Object.entries(spendingsGroupByDate).map(([groupByDate, dateSpendings]) => {
    const { expenses, incomes } = summarizeByCurrency(dateSpendings);

    return (
      <div key={groupByDate}>
        <DayHeader>
          <DayLeft>
            {groupByDate}
            <span className="weekday">{weekdayLabel(dateSpendings[0].date)}</span>
          </DayLeft>

          <DayTotals>
            {expenses.map(({ currency, total }) => (
              <TotalLine key={`expense-${currency}`}>
                <span className="label">支出</span>-{formatAmount(total)} {currency}
              </TotalLine>
            ))}
            {incomes.map(({ currency, total }) => (
              <TotalLine key={`income-${currency}`} className="income">
                <span className="label">收入</span>+{formatAmount(total)} {currency}
              </TotalLine>
            ))}
          </DayTotals>
        </DayHeader>

        {dateSpendings.map((spending) => {
          const { id, date, category, currency, amount, note } = spending;
          return (
            <SpendingCard
              key={id}
              date={date}
              category={category.name}
              categoryType={category.type}
              currency={currency.name}
              amount={amount}
              note={note}
              onDelete={() => onDelete(id)}
              onUpdate={() => onUpdate(spending)}
            />
          );
        })}
      </div>
    );
  });
}

SpendingList.propTypes = {
  spendings: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
